import { AfterViewInit, Component, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { TermComponent } from '../term/term.component';
import { instantValidationThrottle, Lien, Order, OrderManagerService, ValidationProblem, ValidationService } from 'reg-hub-common';

@Component({
    selector: 'app-base-lien-details',
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class BaseLienDetailsComponent extends SteppedComponent implements AfterViewInit {
  @ViewChild(TermComponent) termComponent!: TermComponent;

  termError$ = new BehaviorSubject<string | null | undefined>(null);
  expiryDateError$ = new BehaviorSubject<string | null | undefined>(null);
  amountError$ = new BehaviorSubject<string | null | undefined>(null);
  trustIndentureError$ = new BehaviorSubject<string | null | undefined>(null);
  additionalInformationError$ = new BehaviorSubject<string | null | undefined>(null);
  registrationNumberError$ = new BehaviorSubject<string | null | undefined>(null);
  isInitialized: boolean = false;

  @Input() isDisabled: boolean = false;
  protected lienDetails!: FormGroup;
  protected selectedOption: string = "term";
  protected term: number | null = null;
  protected isHistorical: boolean = false;

  /** Set lienDetails form group control */
  protected abstract buildForm(): FormGroup;

  constructor(
    protected formBuilder: FormBuilder,
    orderManager: OrderManagerService,
    validationService: ValidationService) {
    super(orderManager, validationService);
  }

  override ngOnInit(): void {
    this.isInitialized = false;
    super.ngOnInit();

    this.isHistorical = (this.order.orderStatusTypeID.toLowerCase() == "historicaldraft");

    this.lienDetails = this.buildForm();

    this.lienDetails.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        instantValidationThrottle())
      .subscribe(() => {
        try {
          if (this.isInitialized == true) {
            let orderToValidate = this.getOrderToValidate();
            this.validate(orderToValidate);
          }
        } catch {

        }
      });

    if (this.isDisabled) {
      this.lienDetails.disable();
    }
    
    const amountControl = this.lienDetails.get('amount');
    if(amountControl && !this.uiConfiguration.lienDetailsConfiguration?.isAmountEditable) {
      amountControl.disable();
    }
  }

  ngAfterViewInit(): void {
    if (this.termComponent) {
      if (this.uiConfiguration.lienDetailsConfiguration?.showInfinityTerm) {
        this.lienDetails.setControl('isInfiniteTerm', this.termComponent.termDetails.get('isInfiniteTerm'), { emitEvent: false });
      }

      if (this.uiConfiguration.lienDetailsConfiguration?.showTerm) {
        this.lienDetails.setControl('term', this.termComponent.termDetails.get('term'), { emitEvent: false });
      }

      if (this.uiConfiguration.lienDetailsConfiguration?.showExpiryDate) {
        this.lienDetails.setControl('expiryDate', this.termComponent.termDetails.get('expiryDate'), { emitEvent: false });
      }
    }

    this.isInitialized = true;
  }

  public override pushErrors(errors: ValidationProblem[] | undefined): void {
    if (!errors) {
      return;
    }

    this.termError$.next(errors?.filter(error => error.path.includes('/term'))?.at(0)?.userFriendlyMessage);
    this.termError$.next(errors?.filter(error => error.path.includes('/expirydate') || error.path.includes('/term'))?.at(0)?.userFriendlyMessage);
    this.amountError$.next(errors?.filter(error => error.path.includes('/amount'))?.at(0)?.userFriendlyMessage);
    this.trustIndentureError$.next(errors?.filter(error => error.path.includes('/trustindenture'))?.at(0)?.userFriendlyMessage);
    this.additionalInformationError$.next(errors?.filter(error => error.path.includes('/additionalinformation'))?.at(0)?.userFriendlyMessage);
  }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    const filteredErrors = errors?.filter(error =>
      error.path.includes('lien') &&
      !error.path.includes('collateral') &&
      !error.path.includes('/sumofhypothec'));
    return filteredErrors?.length ?? 0 > 0 ? StepValidationState.ValidationError : StepValidationState.ValidationSuccess;
  }

  protected getOrderToValidate(): Order {
    let orderToValidate = this.copyOrder(this.order);
    this.mapFormToLien(orderToValidate.lien);

    return orderToValidate;
  }

  protected mapFormToLien(lien: Lien | null | undefined): void {
    if (!lien) {
      return;
    }

    for (const key in this.lienDetails.controls) {
      if (key in lien) {
        (lien as any)[key] = this.lienDetails.controls[key].value;
      }
    }
  }

  onTermSelectionChange(event: MatRadioChange): void {
    if (event.value === true) {
      this.order.lien!.expiryDate = null;
      this.order.lien!.term = null;
      this.order.lien!.isInfiniteTerm = true;
    } else {
      this.order.lien!.isInfiniteTerm = false;
    }
  }

  public override onSaving(): void {
    this.mapFormToLien(this.order.lien);

    if (this.order.lien?.isInfiniteTerm === true) {
      this.order.lien.term = null;
    }
  }
}